import React, { useState } from 'react';
import { Popover } from 'lib/components/Popover/Popover';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ColumnSelectOption } from 'lib/components/ColumnSelect';
import { ColumnSelectItem } from 'lib/components/ColumnSelect/ColumnSelectItem';
import { TextField } from 'lib/components/TextField';
import { ColumnButton } from 'lib/components/ColumnButton/ColumnButton';
import { InputAccessories } from 'lib/components/InputAccessories';
import classNames from 'classnames';

interface OrganizationMultiSelectPopoverProps {
  value: string[];
  options: ColumnSelectOption<string>[];
  onChange: (value: string[]) => void;
}

export function OrganizationMultiSelectPopover({
  value,
  options,
  onChange
}: OrganizationMultiSelectPopoverProps) {
  const [search, setSearch] = useState('');

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectAll = () => {
    const filteredValues = filteredOptions.map(opt => opt.value);
    const newValues = Array.from(new Set([...value, ...filteredValues]));
    onChange(newValues);
  };

  const handleSelectNone = () => {
    const newValues = value.filter(
      val => !filteredOptions.some(opt => opt.value === val)
    );
    onChange(newValues);
  };

  const handleToggle = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const placeholder = 'Select Newspapers';
  const getSelectLabelValue = () => {
    if (value.length === 0) return placeholder;
    if (value.length === 1) {
      const opt = options.find(o => o.value === value[0]);
      return opt ? opt.label : placeholder;
    }
    return `${value.length} selected`;
  };

  return (
    <InputAccessories
      id="organization-multiselect"
      labelText={'Select Newspapers'}
    >
      <div className="min-w-[220px]">
        <Popover
          id="organization-multiselect-popover"
          headerText={undefined}
          activator={
            <button
              id="organization-multiselect-activator"
              type="button"
              className="flex items-center bg-white w-full rounded-md font-medium border p-3 text-sm border-column-gray-200 focus:border-column-primary-500 focus:shadow-outline-column-primary"
            >
              <span
                className={classNames('truncate text-sm', {
                  'text-column-gray-300': value.length === 0
                })}
              >
                {getSelectLabelValue()}
              </span>
              <MagnifyingGlassIcon className="h-4 w-4 ml-auto text-column-gray-300" />
            </button>
          }
          alignment="right"
        >
          <div className="w-80 p-2 max-h-80 overflow-y-auto">
            <div className="flex flex-col gap-2 mb-2">
              <TextField
                type="text"
                id="organization-search"
                labelText=""
                placeholder="Search"
                value={search}
                onChange={setSearch}
                prefix={
                  <MagnifyingGlassIcon className="h-5 w-5 text-column-gray-300 stroke-2" />
                }
                suffix={
                  search ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      className="ml-2 p-1 rounded-full hover:bg-column-gray-100 focus:outline-none"
                      onClick={() => setSearch('')}
                    >
                      <XMarkIcon className="h-4 w-4 text-column-gray-300" />
                    </button>
                  ) : null
                }
              />
              <div className="flex gap-2">
                <ColumnButton
                  type="button"
                  buttonText="Select All"
                  tertiary
                  fullWidth
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={filteredOptions.length === 0}
                />
                <ColumnButton
                  type="button"
                  buttonText="Select None"
                  tertiary
                  fullWidth
                  size="sm"
                  onClick={handleSelectNone}
                  disabled={filteredOptions.length === 0}
                />
              </div>
            </div>
            {filteredOptions.length === 0 ? (
              <div className="text-center py-2 text-column-gray-300 text-m">
                No organizations found
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {filteredOptions.map(option => (
                  <div
                    key={option.value}
                    onClick={() => handleToggle(option.value)}
                    className="cursor-pointer"
                  >
                    <ColumnSelectItem
                      option={option}
                      active={false}
                      selected={value.includes(option.value)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        </Popover>
      </div>
    </InputAccessories>
  );
}
